import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import type { InvoiceReminder } from "@/hooks/useInvoiceReminders";

interface SendReminderDialogProps {
  reminder: InvoiceReminder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reminder_id: string, custom_message?: string) => void;
  isLoading: boolean;
}

export const SendReminderDialog = ({
  reminder,
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: SendReminderDialogProps) => {
  const [customMessage, setCustomMessage] = useState("");
  const [subject, setSubject] = useState("");

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setCustomMessage("");
      setSubject("");
    }
    onOpenChange(newOpen);
  };

  const handleConfirm = () => {
    if (!reminder) return;
    onConfirm(reminder.id, customMessage || undefined);
  };

  if (!reminder || !reminder.invoices) return null;

  const invoice = reminder.invoices;
  const client = invoice.clients;
  const dueDate = new Date(reminder.due_date);
  const formattedDueDate = dueDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const defaultSubject = reminder.email_subject || 
    `Payment Reminder: Invoice #${invoice.invoice_number} due in ${reminder.days_until_due} days`;

  const defaultMessage = `This is a friendly reminder that Invoice #${invoice.invoice_number} for ₹${invoice.total?.toLocaleString('en-IN')} is due in ${reminder.days_until_due} days (on ${formattedDueDate}).

If you have any questions or have already made the payment, please let us know.`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Send Payment Reminder</DialogTitle>
          <DialogDescription>
            Review and customize the reminder email before sending
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Invoice Preview */}
          <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
            <h3 className="font-semibold">Invoice Details</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Invoice #:</span>
                <span className="ml-2 font-medium">{invoice.invoice_number}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Amount:</span>
                <span className="ml-2 font-medium">
                  ₹{invoice.total?.toLocaleString('en-IN')}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Client:</span>
                <span className="ml-2 font-medium">
                  {client?.name || "Unknown"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Due Date:</span>
                <span className="ml-2 font-medium">{formattedDueDate}</span>
              </div>
            </div>
          </div>

          {/* Email Preview */}
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="recipient">To</Label>
              <Input
                id="recipient"
                value={client?.email || reminder.recipient_email || ""}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={subject || defaultSubject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={defaultSubject}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder={defaultMessage}
                rows={8}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use the default message template
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send Reminder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};